/**
 * storyFiltersApi — fetch functions for `/api/v2/stories/:storyId/filters`.
 *
 * Toggle via REACT_APP_STORY_FILTERS_API: unset / "live" → real HTTP,
 * "mock" → storyFilters.mock.ts. Flip it together with
 * REACT_APP_STORY_ITEMS_API, or the charts won't match the chips.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import {
  mockCreateStoryFilter,
  mockDeleteStoryFilter,
  mockFetchStoryFilters,
  mockKeepStoryFilter,
  mockPatchStoryFilter,
  mockRewindStoryFilters,
} from "./storyFilters.mock";
import type {
  StoryFilterCreateRequestApi,
  StoryFilterListParams,
  StoryFilterListResponseApi,
  StoryFilterMutationResponseApi,
  StoryFilterPatchRequestApi,
  StoryFilterRewindRequestApi,
} from "./storyFilters.types";

const isMockStoryFilters = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_FILTERS_API);

/**
 * Built by hand because `apiService.get` serialises arrays as `kind[]=a`,
 * while the backend reads a repeated `kind=a&kind=b`.
 */
const withQuery = (path: string, { canvasItemId, kinds }: StoryFilterListParams): string => {
  const query = new URLSearchParams();
  if (canvasItemId) query.append("canvas_item_id", canvasItemId);
  kinds?.forEach((kind) => query.append("kind", kind));
  const text = query.toString();
  return text ? `${path}?${text}` : path;
};

/** Every filter on the story, plus whether the caller may change them. */
export async function fetchStoryFilters(
  storyId: string,
  params: StoryFilterListParams = {},
): Promise<StoryFilterListResponseApi> {
  if (isMockStoryFilters()) return mockFetchStoryFilters(storyId, params);

  return apiService.get<StoryFilterListResponseApi>(
    withQuery(API_ENDPOINTS.stories.filters(storyId), params),
  );
}

/**
 * `POST /filters` (201): add one filter. The response carries the whole list
 * and the items to refetch; a 403, 404, 409 or 422 rejects with its status.
 */
export async function createStoryFilter(
  storyId: string,
  body: StoryFilterCreateRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  if (isMockStoryFilters()) return mockCreateStoryFilter(storyId, body);

  return apiService.post<StoryFilterMutationResponseApi>(API_ENDPOINTS.stories.filters(storyId), body);
}

/**
 * `PATCH /filters/{id}`: change a filter's comparator or value. The id is
 * URL-encoded by the endpoint, so a `baseline:{item}:{id}` id is safe. A 409
 * means the step is no longer the latest (or is a drill step); the caller refetches.
 */
export async function patchStoryFilter(
  storyId: string,
  filterId: string,
  body: StoryFilterPatchRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  if (isMockStoryFilters()) return mockPatchStoryFilter(storyId, filterId, body);

  return apiService.patch<StoryFilterMutationResponseApi>(
    API_ENDPOINTS.stories.filter(storyId, filterId),
    body,
  );
}

/**
 * `DELETE /filters/{id}` (200 with a body, not 204): the response carries the
 * whole list and the items to refetch, like every write. A 409 means the step
 * is not the latest in the trail; the caller refetches the list.
 */
export async function deleteStoryFilter(
  storyId: string,
  filterId: string,
): Promise<StoryFilterMutationResponseApi> {
  if (isMockStoryFilters()) return mockDeleteStoryFilter(storyId, filterId);

  return apiService.delete<StoryFilterMutationResponseApi>(API_ENDPOINTS.stories.filter(storyId, filterId));
}

/**
 * `POST /filters/rewind`: drop every trail step after `to_filter_id`, or the whole
 * trail when it is null. `expected_latest_id` is always sent; a 409 means the trail
 * moved under the caller, who refetches the list.
 */
export async function rewindStoryFilters(
  storyId: string,
  body: StoryFilterRewindRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  if (isMockStoryFilters()) return mockRewindStoryFilters(storyId, body);

  return apiService.post<StoryFilterMutationResponseApi>(API_ENDPOINTS.stories.filtersRewind(storyId), body);
}

/**
 * `POST /filters/{id}/keep`: the Story's owner keeps a personal filter for everyone. A 404 means
 * the caller isn't the owner or the id is a baseline; a 409 means the Story already has one there.
 */
export async function keepStoryFilter(storyId: string, filterId: string): Promise<StoryFilterMutationResponseApi> {
  if (isMockStoryFilters()) return mockKeepStoryFilter(storyId, filterId);

  return apiService.post<StoryFilterMutationResponseApi>(API_ENDPOINTS.stories.filterKeep(storyId, filterId), {});
}
